<template>
  <div class="card">
    <div class="px-1">
      <DateQuerySetter
          btnTitle="Go"
          @onClickGo="getRecentActivityData"
      >
        <div class="col-12 col-sm-6 col-md-4 col-lg-3 mr-top-1">
          <label class="form-label">Campaign / Project</label>
          <AsyncSelect
              placeholder="Project"
              v-model="projectId"
              :api-service="fetchProjects"
              :reduce="text => text.id"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3 mr-top-1">
          <label class="form-label">Source</label>
          <v-select
              placeholder="Select Source"
              :options="dealSourceList" v-model="dealSourceId"
              :reduce="option => option.id"
              label="name"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-2 mr-top-1">
          <label class="form-label">Select Owner</label>
          <AsyncSelect
              placeholder="Owner"
              v-model="dealOwnerId"
              :api-service="fetchContactProfiles"
              :reduce="text => text.id"
              :additional-query="{ type: 'contact_owner' }"
          />
        </div>

        <div v-if="permittedAccess.is_all_access" class="col-12 col-sm-6 col-md-4 col-lg-3 mr-top-1">
          <label class="form-label">Select Assign to</label>
          <AsyncSelect
              placeholder="Assign to"
              v-model="dealAssignId"
              :api-service="fetchContactProfiles"
              :reduce="text => text.id"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3 mr-top-1">
        <label class="form-label">Business Unit</label>
          <v-select
              placeholder="Select Business"
              v-model="businessId"
              :options="business"
              label="name"
              :reduce="name => name.id"
          />
        </div>

        <div class="col-12 col-sm-6 col-md-4 col-lg-3 mr-top-1">
          <label class="form-label"> Product/Service </label>
          <AsyncSelect
              placeholder="Select Product/Service"
              :api-service="fetchProductList"
              v-model="productIds"
              label="text"
              :reduce="text => text"
              :format-label="option => option.text"
              :multiple="true"
          />
        </div>
      </DateQuerySetter>
    </div>

    <div class="col-12 mb-4">
      <div class="card" style="min-height: 200px">
        <div v-if="tableItems.length > 0" class="stages-grid">
          <div
              v-for="(item, i) in tableItems"
              :key="i"
              class="stage-card"
          >
            <div class="stage-header">
              <span class="stage-label">{{ item.name }}</span>
            </div>

            <div class="stage-count cursor-pointer" @click="onClickDetailsPage(item)">
              {{ item.sale_pipelines && item.sale_pipelines.length }}
            </div>

            <div class="leads-list">
              <div v-for="(pipeline, index) in item.sale_pipelines.slice(0,5)" :key="pipeline.id">
                <div class="lead-item">
                  {{ pipeline.deal_title }} ({{ pipeline.contact_profile_name}})
                </div>
              </div>
            </div>

            <div class="stage-header text-right cursor-pointer" @click="onClickDetailsPage(item)">
              <span class="stage-label">See More..</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Loader v-if="loading" />
  </div>
</template>

<script setup>
import {computed, inject, onMounted, ref} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import handleAdmin from '@/services/modules/admin/admin';
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handleProjects from "@/services/modules/procurement/project";
import handleContact from "@/services/modules/contact";
import handlePurchase from '@/services/modules/purchase';
import DateQuerySetter from '@/components/atom/DateQuerySetter.vue';
import handleCBusinesses from '@/services/modules/businesses'
import handleHospital from '@/services/modules/hospital';

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false)
const tableItems = ref([]);
const params = ref({
  offset: 10,
});
const projectId = ref(null);
const dealOwnerId = ref(null);
const dealAssignId = ref(null);
const productIds = ref([]);
const start = computed(() => route.query.start);
const end = computed(() => route.query.end);
const businessId = ref(null);
const dealSourceId = ref(null);
const dealSourceList = ref([]);
let business = ref([])
let permittedAccess = ref({})

const {fetchBusinessList} = handleCBusinesses()
const {
  fetchCrmRecentActivityData
} = handleAdmin();
const { fetchProjects } = handleProjects();
const { fetchContactProfiles } = handleContact();
const { fetchProductList } = handlePurchase();
const { fetchNoteMasterList } = handleHospital();

const getRecentActivityData = async () => {
  const query = getQuery();
  loading.value = true
  tableItems.value = []
  await fetchCrmRecentActivityData(query).then(res => {
    if (!res.status){
      return
    }
    tableItems.value = res.data.data;
    permittedAccess.value = res.data.permitted_access;
  }).catch(err => {
    console.log(err)
  }).finally(() => {
    loading.value = false
  })
}

const onClickDetailsPage = (item) => {
  router.push({
    name: 'sale-pipeline-details',
    params: route.params,
    query: {
      ...route.query,
      type: 'recent_activity',
      deal_stage_id: item.id
    }
  })
}

const getQuery = () => {
  let query = `?company_id=${route.params.companyId}`;
  if (start) query += '&start_date=' + start.value
  if (end) query += '&end_date=' + end.value
  if(projectId.value) query += '&project_master_id=' + projectId.value
  if(dealOwnerId.value) query += '&deal_owner_id=' + dealOwnerId.value
  if(dealAssignId.value) query += '&deal_assign_id=' + dealAssignId.value
  if(dealSourceId.value) query += '&deal_source_id=' + dealSourceId.value
  if(businessId.value) query += '&business_id=' + businessId.value
  if (productIds.value && productIds.value.length) {
    const ids = productIds.value.map(p => p.id).join(',');
    query += `&product_ids=${ids}`;
  }
  return query;
}

const getDealList = async (noteType, targetList) => {
  const query = `?company_id=${route.params.companyId}&note_type=${noteType}`;
  try {
    const res = await fetchNoteMasterList(query);
    if (!res.status) {
      return showError(res.message);
    }
    targetList.value = res.data.data;
  } catch (err) {
    console.error(err);
  }
};

onMounted(() => {
  const query = `?company_id=${route.params.companyId}`;
  const businessRes = fetchBusinessList(query)

  Promise.all([
    getDealList('deal_source', dealSourceList),
    getRecentActivityData(),
    businessRes.then(res=> {
      if(res.data) business.value = res.data
    })
  ])
})
</script>

<style scoped>
.stages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  padding: 1.5rem;
}

.stage-card {
  background: white;
  border-radius: 0.25rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #dee2e6;
  display: flex;
  flex-direction: column;
}

.stage-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  border-color: #c3cad4;
}

/* Stage Header */
.stage-header {
  background: linear-gradient(135deg, #f0f2f5 0%, #e8ebf0 100%);
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #dee2e6;
}

.stage-label {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  letter-spacing: 0.3px;
}

/* Stage Count */
.stage-count {
  font-size: 3.5rem;
  font-weight: 800;
  color: #7367f0;
  text-align: center;
  padding: 1.5rem 1rem;
  background: linear-gradient(180deg, #ffffff 0%, #f7f8fa 100%);
  letter-spacing: -1px;
  line-height: 1;
  border-bottom: 1px solid #dee2e6;
}

/* Leads List */
.leads-list {
  flex-grow: 1;
  padding: 1rem;
  background: white;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-height: 90px;
}

.lead-item {
  font-size: 0.85rem;
  color: #666;
  padding: 0.5rem 0;
  line-height: 1.4;
  font-weight: 400;
}

.lead-item.placeholder {
  color: #999;
  font-style: italic;
  text-align: center;
  margin: auto 0;
}

/* Button Override for better spacing */
.stage-card .btn {
  border-radius: 0;
  margin: 0;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 0.4px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.stage-card .btn:hover {
  transform: translateY(-1px);
}

.stage-card .btn:active {
  transform: translateY(0);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .stages-grid {
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 1.25rem;
    padding: 1.25rem;
  }

  .stage-count {
    font-size: 3rem;
  }
}

@media (max-width: 768px) {
  .stages-grid {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 1rem;
    padding: 1rem;
  }

  .stage-count {
    font-size: 2.5rem;
    padding: 1.25rem 0.75rem;
  }

  .stage-header {
    padding: 0.65rem 0.85rem;
  }

  .stage-label {
    font-size: 0.85rem;
  }

  .leads-list {
    padding: 0.85rem;
    min-height: 80px;
  }

  .lead-item {
    font-size: 0.8rem;
  }

  .stage-card .btn {
    padding: 0.75rem 1.25rem;
    font-size: 0.85rem;
  }
}

@media (max-width: 576px) {
  .stages-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    padding: 0.75rem;
  }

  .stage-count {
    font-size: 2rem;
    padding: 1rem 0.5rem;
  }

  .stage-header {
    padding: 0.6rem 0.75rem;
  }

  .stage-label {
    font-size: 0.8rem;
  }

  .leads-list {
    padding: 0.75rem;
    min-height: 70px;
    gap: 0.3rem;
  }

  .lead-item {
    font-size: 0.75rem;
    padding: 0.3rem 0;
  }

  .stage-card .btn {
    padding: 0.65rem 1rem;
    font-size: 0.8rem;
  }
}

.mr-top-1 {
  margin-top: 0.5% !important;
}
</style>